import type { SessionTimingConfig } from "./sessionConfig.js";
import type {
  AuthCapabilities,
  LoginCredentials,
  RegistrationRequest,
  SessionApiError,
  SessionEstablished,
  SessionUnauthorizedCode,
  SessionUser,
  WsAuthAdapter,
} from "./sessionTypes.js";

const DEFAULT_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api";

const UNAUTHORIZED_CODES = new Set<SessionUnauthorizedCode>([
  "auth.missing",
  "auth.expired",
  "auth.revoked",
  "auth.deactivated",
  "auth.invalid",
  "auth.invalid_credentials",
  "auth.refresh_missing",
  "auth.refresh_invalid",
]);

export class SessionApiException extends Error {
  readonly info: SessionApiError;

  constructor(info: SessionApiError) {
    super(info.kind);
    this.name = "SessionApiException";
    this.info = info;
  }
}

export class SessionApiConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SessionApiConfigError";
  }
}

export interface SessionApiOptions {
  readonly baseUrl?: string;
  readonly origin?: string;
  readonly fetchImpl?: typeof fetch;
  readonly timings: Readonly<SessionTimingConfig>;
  readonly csrfToken?: () => string | null;
  readonly wsAuthAdapter: WsAuthAdapter;
}

interface ProblemWire {
  readonly code?: unknown;
  readonly errors?: unknown;
}

function containsUnsafeBaseCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.charCodeAt(0);
    if (code <= 0x20 || code === 0x7f || character === "\\") return true;
  }
  return false;
}

function browserOrigin(explicit: string | undefined): string {
  const raw = explicit ?? globalThis.location?.origin;
  if (!raw) throw new SessionApiConfigError("browser origin is required");

  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    throw new SessionApiConfigError("browser origin must be an absolute URL");
  }
  if (
    (url.protocol !== "http:" && url.protocol !== "https:") ||
    url.origin === "null"
  ) {
    throw new SessionApiConfigError("browser origin must use http or https");
  }
  return url.origin;
}

function normalizeBaseUrl(raw: string, origin: string): string {
  if (raw === "" || raw.startsWith("//") || containsUnsafeBaseCharacter(raw)) {
    throw new SessionApiConfigError("API base must be a safe same-origin URL");
  }

  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    throw new SessionApiConfigError("API base is malformed");
  }
  if (
    url.origin !== origin ||
    url.username ||
    url.password ||
    url.search ||
    url.hash
  ) {
    throw new SessionApiConfigError("API base must stay on the browser origin");
  }

  const path = url.pathname.replace(/\/+$/u, "");
  const absolute = /^[a-z][a-z\d+.-]*:/iu.test(raw);
  return absolute ? `${origin}${path}` : path;
}

function defaultCsrfToken(): string | null {
  if (typeof document === "undefined") return null;
  for (const chunk of document.cookie.split(";")) {
    const [rawName, ...rest] = chunk.trim().split("=");
    if (rawName === "gocanvas_csrf" || rawName === "__Host-gocanvas_csrf") {
      const value = rest.join("=");
      try {
        return decodeURIComponent(value);
      } catch {
        return null;
      }
    }
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseUser(value: unknown): SessionUser | null {
  if (!isRecord(value)) return null;
  if (
    typeof value.id !== "string" ||
    value.id === "" ||
    typeof value.email !== "string" ||
    typeof value.display_name !== "string" ||
    typeof value.identity_provider !== "string" ||
    typeof value.is_active !== "boolean" ||
    (value.avatar_url !== undefined && typeof value.avatar_url !== "string")
  ) {
    return null;
  }
  return {
    id: value.id,
    email: value.email,
    display_name: value.display_name,
    identity_provider: value.identity_provider,
    is_active: value.is_active,
    ...(typeof value.avatar_url === "string" && {
      avatar_url: value.avatar_url,
    }),
  };
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new SessionApiException({
      kind: "server",
      status: response.status,
      code: "auth.contract",
    });
  }
}

function contractError(status: number): SessionApiException {
  return new SessionApiException({
    kind: "server",
    status,
    code: "auth.contract",
  });
}

function parseValidationFields(raw: unknown): Readonly<Record<string, string>> {
  if (!isRecord(raw)) return Object.freeze({});
  const fields: Record<string, string> = {};
  for (const [name, value] of Object.entries(raw)) {
    if (typeof value === "string") {
      fields[name] = value;
    } else if (Array.isArray(value) && typeof value[0] === "string") {
      fields[name] = value[0];
    }
  }
  return Object.freeze(fields);
}

function unauthorizedCode(raw: unknown): SessionUnauthorizedCode {
  return typeof raw === "string" &&
    UNAUTHORIZED_CODES.has(raw as SessionUnauthorizedCode)
    ? (raw as SessionUnauthorizedCode)
    : "auth.invalid";
}

function retryAfter(response: Response, fallback: number): number {
  const raw = response.headers.get("Retry-After");
  if (raw !== null && /^\d+$/u.test(raw)) {
    const parsed = Number(raw);
    if (Number.isSafeInteger(parsed) && parsed >= 0) return parsed;
  }
  return fallback;
}

function isTimeout(signal: AbortSignal, cause: unknown): boolean {
  const reason = signal.reason;
  const candidate = signal.aborted ? reason : cause;
  return (
    (candidate instanceof DOMException && candidate.name === "TimeoutError") ||
    (isRecord(candidate) && candidate.name === "TimeoutError")
  );
}

export class SessionApi {
  readonly #baseUrl: string;
  readonly #fetchImpl: typeof fetch;
  readonly #timings: Readonly<SessionTimingConfig>;
  readonly #csrfToken: () => string | null;
  readonly #wsAuthAdapter: WsAuthAdapter;

  constructor(options: SessionApiOptions) {
    const origin = browserOrigin(options.origin);
    this.#baseUrl = normalizeBaseUrl(options.baseUrl ?? DEFAULT_BASE, origin);
    this.#fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.#timings = options.timings;
    this.#csrfToken = options.csrfToken ?? defaultCsrfToken;
    this.#wsAuthAdapter = options.wsAuthAdapter;
  }

  async me(signal: AbortSignal): Promise<SessionUser> {
    const response = await this.#request("/v1/users/me", "GET", signal);
    if (response.status !== 200) throw contractError(response.status);
    const user = parseUser(await readJson(response));
    if (!user) throw contractError(response.status);
    return user;
  }

  async capabilities(signal: AbortSignal): Promise<AuthCapabilities> {
    const response = await this.#request("/v1/auth/capabilities", "GET", signal);
    if (response.status !== 200) throw contractError(response.status);
    const value = await readJson(response);
    if (
      !isRecord(value) ||
      typeof value.signup_enabled !== "boolean" ||
      !isRecord(value.password)
    ) {
      throw contractError(response.status);
    }
    const min = value.password.min_length;
    const max = value.password.max_length;
    if (
      !Number.isInteger(min) ||
      !Number.isInteger(max) ||
      (min as number) <= 0 ||
      (max as number) < (min as number)
    ) {
      throw contractError(response.status);
    }
    return {
      signup_enabled: value.signup_enabled,
      password: { min_length: min as number, max_length: max as number },
    };
  }

  async login(
    request: LoginCredentials,
    signal: AbortSignal,
  ): Promise<SessionEstablished> {
    const response = await this.#request(
      "/v1/auth/login",
      "POST",
      signal,
      request,
    );
    if (response.status !== 200) throw contractError(response.status);
    return this.#acceptEstablishment(response, signal);
  }

  async register(
    request: RegistrationRequest,
    signal: AbortSignal,
  ): Promise<SessionEstablished> {
    const response = await this.#request(
      "/v1/auth/register",
      "POST",
      signal,
      request,
    );
    if (response.status !== 201) throw contractError(response.status);
    return this.#acceptEstablishment(response, signal);
  }

  async refresh(signal: AbortSignal): Promise<SessionEstablished> {
    const csrf = await this.#ensureCsrf(signal);
    const response = await this.#request(
      "/v1/auth/refresh",
      "POST",
      signal,
      undefined,
      csrf,
    );
    if (response.status !== 200) throw contractError(response.status);
    return this.#acceptEstablishment(response, signal);
  }

  async logout(signal: AbortSignal): Promise<void> {
    const csrf = await this.#ensureCsrf(signal);
    const response = await this.#request(
      "/v1/auth/logout",
      "POST",
      signal,
      undefined,
      csrf,
    );
    if (response.status !== 204) throw contractError(response.status);
  }

  async #acceptEstablishment(
    response: Response,
    signal: AbortSignal,
  ): Promise<SessionEstablished> {
    const value = await readJson(response);
    signal.throwIfAborted();
    if (
      !isRecord(value) ||
      Object.prototype.hasOwnProperty.call(value, "refresh_token")
    ) {
      throw contractError(response.status);
    }
    if (
      typeof value.access_token !== "string" ||
      value.access_token === "" ||
      value.token_type !== "Bearer" ||
      typeof value.expires_in !== "number" ||
      !Number.isFinite(value.expires_in) ||
      value.expires_in <= 0
    ) {
      throw contractError(response.status);
    }

    let user: SessionUser | undefined;
    if (value.user !== undefined) {
      user = parseUser(value.user) ?? undefined;
      if (!user) throw contractError(response.status);
    }

    try {
      this.#wsAuthAdapter.accept(value.access_token, value.expires_in);
    } catch {
      throw contractError(response.status);
    }

    return { expiresIn: value.expires_in, ...(user && { user }) };
  }

  async #request(
    path: string,
    method: "GET" | "POST",
    signal: AbortSignal,
    body?: LoginCredentials | RegistrationRequest,
    csrfOverride?: string,
  ): Promise<Response> {
    const headers = new Headers({ Accept: "application/json" });
    if (method === "POST") {
      const csrf = csrfOverride ?? this.#csrfToken();
      if (csrf) headers.set("X-CSRF-Token", csrf);
      if (body !== undefined) headers.set("Content-Type", "application/json");
    }

    let response: Response;
    try {
      signal.throwIfAborted();
      response = await this.#fetchImpl(`${this.#baseUrl}${path}`, {
        method,
        credentials: "include",
        signal,
        headers,
        ...(body !== undefined && { body: JSON.stringify(body) }),
      });
    } catch (cause) {
      if (signal.aborted && !isTimeout(signal, cause)) throw signal.reason;
      throw new SessionApiException({
        kind: isTimeout(signal, cause) ? "timeout" : "network",
      });
    }

    if (!response.ok) await this.#throwResponseError(response);
    return response;
  }

  async #ensureCsrf(signal: AbortSignal): Promise<string> {
    const existing = this.#csrfToken();
    if (existing) return existing;
    const response = await this.#request("/v1/auth/csrf", "GET", signal);
    if (response.status !== 200) throw contractError(response.status);
    const value = await readJson(response);
    if (
      !isRecord(value) ||
      typeof value.csrf_token !== "string" ||
      value.csrf_token === ""
    ) {
      throw contractError(response.status);
    }
    return value.csrf_token;
  }

  async #throwResponseError(response: Response): Promise<never> {
    let problem: ProblemWire = {};
    try {
      const value = await response.json();
      if (isRecord(value)) problem = value;
    } catch {
      // HTTP status remains authoritative when the problem body is absent/malformed.
    }

    switch (response.status) {
      case 400:
      case 422:
        throw new SessionApiException({
          kind: "validation",
          fields: parseValidationFields(problem.errors),
        });
      case 401:
        throw new SessionApiException({
          kind: "unauthorized",
          code: unauthorizedCode(problem.code),
        });
      case 403:
        throw new SessionApiException({
          kind: "forbidden",
          code:
            typeof problem.code === "string" ? problem.code : "auth.forbidden",
        });
      case 409:
        throw new SessionApiException({
          kind: "conflict",
          code:
            typeof problem.code === "string"
              ? problem.code
              : "auth.registration_unavailable",
        });
      case 429:
        throw new SessionApiException({
          kind: "rate-limited",
          retryAfterSec: retryAfter(
            response,
            this.#timings.rateLimitFallbackSec,
          ),
        });
      default:
        throw new SessionApiException({
          kind: "server",
          status: response.status,
          ...(typeof problem.code === "string" && { code: problem.code }),
        });
    }
  }
}

export type {
  AuthCapabilities,
  LoginCredentials,
  RegistrationRequest,
  SessionApiError,
  SessionEstablished,
  SessionUser,
  WsAuthAdapter,
} from "./sessionTypes.js";
